// Geocoding utility using OpenStreetMap Nominatim API

/** English place names so stored addresses match regardless of browser UI language. */
export const NOMINATIM_HEADERS: Record<string, string> = {
  Accept: 'application/json',
  'Accept-Language': 'en',
  'User-Agent': 'MindfulFamily/1.0 (https://mindful.family)',
};

export interface GeocodeResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address?: {
    country_code?: string;
    country?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
  };
}

export interface LocationCoords {
  latitude: number;
  longitude: number;
}

export interface ReverseGeocodeResult {
  display_name: string;
  address?: GeocodeResult['address'];
}

/**
 * Search for addresses using OpenStreetMap Nominatim API (English labels).
 */
export async function searchAddress(query: string): Promise<GeocodeResult[]> {
  if (query.length < 3) {
    return [];
  }

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}&limit=5`,
      { headers: NOMINATIM_HEADERS }
    );

    if (!response.ok) throw new Error('Search failed');

    const data: GeocodeResult[] = await response.json();
    return data;
  } catch (error) {
    console.error('Geocoding error:', error);
    return [];
  }
}

/**
 * Reverse geocode coordinates (English labels).
 */
export async function reverseGeocode(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`,
      { headers: NOMINATIM_HEADERS }
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || typeof data.display_name !== 'string') return null;
    return {
      display_name: data.display_name,
      address: data.address,
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

/**
 * Geocode an address to coordinates
 */
export async function geocodeAddress(address: string): Promise<LocationCoords | null> {
  try {
    const results = await searchAddress(address);
    if (results.length === 0) {
      return null;
    }

    const firstResult = results[0];
    return {
      latitude: parseFloat(firstResult.lat),
      longitude: parseFloat(firstResult.lon),
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}
